/** 支持的文本格式 */
export type Format =
  | 'plain'
  | 'json'
  | 'xml'
  | 'html'
  | 'sql'
  | 'yaml'
  | 'markdown'
  | 'css'
  | 'javascript';

/** 格式对应的显示名称 */
export const FORMAT_LABELS: Record<Format, string> = {
  plain: 'Plain Text',
  json: 'JSON',
  xml: 'XML',
  html: 'HTML',
  sql: 'SQL',
  yaml: 'YAML',
  markdown: 'Markdown',
  css: 'CSS',
  javascript: 'JavaScript / TypeScript',
};

/** 格式列表（用于下拉框） */
export const FORMATS: Format[] = [
  'plain', 'json', 'xml', 'html', 'sql', 'yaml', 'markdown', 'css', 'javascript',
];

/* ========== 自动检测 ========== */

/**
 * 自动检测文本格式
 * 按优先级从高到低检测，首个匹配即返回
 */
export function detectFormat(text: string): Format {
  const trimmed = text.trim();
  if (!trimmed) return 'plain';

  // 1. JSON: 以 { 或 [ 开头且能 parse
  if (/^\s*(?:\{|\[)/.test(trimmed)) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // 不是合法 JSON，继续检测
    }
  }

  // 2. XML/HTML
  if (/^\s*<\?xml|^\s*<!DOCTYPE/i.test(trimmed)) return 'xml';
  if (/^\s*<!DOCTYPE\s+html|^\s*<html/i.test(trimmed)) return 'html';
  // 大量标签模式
  const tagCount = (trimmed.match(/<\/?[a-zA-Z][a-zA-Z0-9]*[\s>]/g) || []).length;
  if (tagCount >= 3) return 'xml';

  // 3. SQL
  if (/^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|DROP)\b/i.test(trimmed)) {
    return 'sql';
  }

  // 4. YAML: 以 --- 开头，或 key: value 模式覆盖大部分行
  if (/^\s*---\s*$/.test(trimmed.split('\n')[0] || '')) return 'yaml';
  const lines = trimmed.split('\n').filter((l) => l.trim());
  const kvLines = lines.filter((l) => /^\s*[\w-]+\s*:\s/.test(l));
  if (lines.length > 0 && kvLines.length / lines.length > 0.5) return 'yaml';

  // 5. Markdown
  if (/^#{1,6}\s|^\*{1,3}[^*]+\*{1,3}|^`{1,3}|^>\s|^-\s|^\[.+\]\(.+\)/m.test(trimmed)) {
    return 'markdown';
  }

  // 6. CSS
  if (/[^{}]*\{[^}]*\}/.test(trimmed) && /:\s*[^;]+;/.test(trimmed)) {
    return 'css';
  }

  // 7. JavaScript/TypeScript
  if (
    /\b(function|const|let|var|import|export|class|=>)\b/.test(trimmed) ||
    /^\/\//.test(trimmed.split('\n').find((l) => l.trim()) || '')
  ) {
    return 'javascript';
  }

  return 'plain';
}

/* ========== Token 正则规则 ========== */

interface TokenRule {
  type: string;
  regex: RegExp;
}

function getRules(format: Format): TokenRule[] {
  switch (format) {
    case 'json':
      return [
        { type: 'string', regex: /"(?:[^"\\]|\\.)*"/g },
        { type: 'number', regex: /-?\b\d+\.?\d*(?:[eE][+-]?\d+)?\b/g },
        { type: 'keyword', regex: /\b(true|false|null)\b/g },
        { type: 'punct', regex: /[{}[\]:,]/g },
      ];

    case 'xml':
    case 'html':
      return [
        { type: 'comment', regex: /<!--[\s\S]*?-->/g },
        { type: 'tag', regex: /<\/?[a-zA-Z][a-zA-Z0-9-]*/g },
        { type: 'attr', regex: /\b[a-zA-Z-]+(?==)/g },
        { type: 'string', regex: /"[^"]*"|'[^']*'/g },
        { type: 'punct', regex: /[<>/=]/g },
      ];

    case 'sql':
      return [
        { type: 'comment', regex: /--[^\n]*/g },
        { type: 'keyword', regex: /\b(SELECT|FROM|WHERE|INSERT|INTO|VALUES|UPDATE|SET|DELETE|CREATE|TABLE|ALTER|DROP|AND|OR|NOT|IN|LIKE|BETWEEN|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AS|ORDER|BY|GROUP|HAVING|LIMIT|OFFSET|UNION|DISTINCT|COUNT|SUM|AVG|MAX|MIN|NULL|IS|PRIMARY|KEY|FOREIGN|INDEX|DEFAULT)\b/gi },
        { type: 'number', regex: /\b\d+\b/g },
        { type: 'string', regex: /'[^']*'|"[^"]*"/g },
      ];

    case 'yaml':
      return [
        { type: 'comment', regex: /#[^\n]*/g },
        { type: 'string', regex: /"[^"]*"|'[^']*'/g },
        { type: 'keyword', regex: /\b(true|false|null|yes|no|on|off)\b/gi },
        { type: 'number', regex: /-?\b\d+\.?\d*\b/g },
        { type: 'punct', regex: /^---|^\.\.\.|[:>{}\-[\]]/g },
      ];

    case 'markdown':
      return [
        { type: 'keyword', regex: /^#{1,6}\s[^\n]*/gm },
        { type: 'string', regex: /`[^`]*`/g },
        { type: 'tag', regex: /\[[^\]]+\]\([^)]+\)/g },   // 链接语法
        { type: 'attr', regex: /\*\*[^*]+\*\*|__[^_]+__/g },  // 粗体
        { type: 'attr', regex: /\*[^*]+\*|_[^_]+_/g },        // 斜体
        { type: 'comment', regex: /^>\s[^\n]*/gm },          // 引用
      ];

    case 'css':
      return [
        { type: 'comment', regex: /\/\*[\s\S]*?\*\//g },
        { type: 'tag', regex: /[.#@]?[a-zA-Z][a-zA-Z0-9_-]*/g },
        { type: 'attr', regex: /[a-z-]+(?=\s*:)/g },
        { type: 'number', regex: /\b\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|s|ms|deg)?\b/g },
        { type: 'string', regex: /"[^"]*"|'[^']*'/g },
        { type: 'punct', regex: /[{}:;,]/g },
      ];

    case 'javascript':
      return [
        { type: 'comment', regex: /\/\/[^\n]*|\/\*[\s\S]*?\*\//g },
        { type: 'string', regex: /`[^`]*`|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g },
        { type: 'keyword', regex: /\b(function|const|let|var|if|else|for|while|do|switch|case|return|new|class|import|export|default|from|async|await|try|catch|throw|typeof|instanceof|interface|type|enum|implements|extends|as)\b/g },
        { type: 'number', regex: /\b\d+\.?\d*(?:[eE][+-]?\d+)?\b/g },
        { type: 'punct', regex: /[{}()[\].,;:=>+\-*/%!<>&|^~?]/g },
      ];

    default:
      return [];
  }
}

/**
 * 对单行文本做语法高亮，返回 HTML 字符串
 * Token 使用 <span class="token-{type}"> 包裹
 */
export function highlightLine(format: Format, line: string): string {
  if (format === 'plain') return escapeHtml(line);

  const rules = getRules(format);
  // 用 position → token type 的映射，按字符位置记录
  const markers: { pos: number; end: number; type: string }[] = [];

  for (const rule of rules) {
    // 重置 lastIndex
    const regex = new RegExp(rule.regex.source, rule.regex.flags);
    let match: RegExpExecArray | null;
    while ((match = regex.exec(line)) !== null) {
      // 检查是否与已有 marker 重叠（先到先得）
      const overlaps = markers.some(
        (m) => match!.index < m.end && match!.index + match![0].length > m.pos
      );
      if (!overlaps) {
        markers.push({
          pos: match.index,
          end: match.index + match[0].length,
          type: rule.type,
        });
      }
      // 防止零宽匹配死循环
      if (match[0].length === 0) break;
    }
  }

  // 按位置排序
  markers.sort((a, b) => a.pos - b.pos);

  // 构建 HTML
  let result = '';
  let cursor = 0;
  for (const m of markers) {
    if (m.pos > cursor) {
      result += escapeHtml(line.slice(cursor, m.pos));
    }
    result += `<span class="token-${m.type}">${escapeHtml(line.slice(m.pos, m.end))}</span>`;
    cursor = m.end;
  }
  result += escapeHtml(line.slice(cursor));

  return result;
}

/** HTML 转义 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
